<script setup lang="ts">
/* eslint-disable vue/html-closing-bracket-newline, vue/no-v-html -- TikTok rich text is sanitized and remote image sources are removed before rendering. */
import type { CascaderNode, CascaderOption, CascaderProps } from 'element-plus';

import type {
  AuthorizedShopOption,
  OperationsCapability,
  ProductManagementCategory,
} from '#/api/tiktok-operations';

import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';

import {
  ElAlert,
  ElButton,
  ElCard,
  ElCascader,
  ElDescriptions,
  ElDescriptionsItem,
  ElDrawer,
  ElDropdown,
  ElDropdownItem,
  ElDropdownMenu,
  ElEmpty,
  ElImage,
  ElInput,
  ElInputNumber,
  ElMessage,
  ElMessageBox,
  ElOption,
  ElPagination,
  ElProgress,
  ElSelect,
  ElTable,
  ElTableColumn,
  ElTabPane,
  ElTabs,
  ElTag,
  ElTooltip,
} from 'element-plus';

import { getShopListApi } from '#/api/core/auth';
import {
  cacheProductManagementImagesApi,
  createProductOperationTaskApi,
  getOperationsCapabilityApi,
  getProductManagementCategoriesApi,
  getProductManagementDetailApi,
  getProductManagementSyncStatusApi,
  searchProductManagementApi,
  syncProductManagementApi,
} from '#/api/tiktok-operations';

import { aggregateProductSyncByShop } from './product-sync-progress';
import { sanitizeProductDescription } from './store-product-display';

type Dict = Record<string, any>;

interface CategoryCascaderOption extends CascaderOption {
  children?: CategoryCascaderOption[];
  label: string;
  leaf?: boolean;
  value: string;
}

interface CategoryCacheEntry {
  expiresAt: number;
  rows: ProductManagementCategory[];
}

const CATEGORY_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const CATEGORY_CACHE_PREFIX = 'speedbee:product-management:categories:';
const categoryMemoryCache = new Map<string, CategoryCacheEntry>();

const router = useRouter();
const shops = ref<AuthorizedShopOption[]>([]);
const capabilityMap = ref<Record<number, OperationsCapability>>({});
const rows = ref<Dict[]>([]);
const selectedRows = ref<Dict[]>([]);
const categoryLoading = ref(false);
const categoryTreeOptions = ref<CategoryCascaderOption[]>([]);
const total = ref(0);
const loading = ref(false);
const syncing = ref(false);
const syncSubmitting = ref(false);
const syncTaskVisible = ref(false);
const advanced = ref(false);
const syncStates = ref<Dict[]>([]);
const detailVisible = ref(false);
const detailLoading = ref(false);
const detail = ref<Dict>();
const detailTab = ref('basic');
const detailRefreshExhausted = ref(false);
const imageCacheLoading = ref(false);
const imageCacheAttempts = ref(0);
const DETAIL_REFRESH_MAX_ATTEMPTS = 60;
let detailRefreshAttempts = 0;
let syncTimer: number | undefined;
let detailRefreshTimer: number | undefined;
let imageCacheTimer: number | undefined;
const statusRefreshing = ref(false);
let disposed = false;
let searchSequence = 0;

const query = reactive({
  shopIds: [] as number[],
  keyword: '',
  statuses: ['ACTIVATE'] as string[],
  categoryKeyword: '',
  categoryIds: [] as string[],
  flashSaleActive: undefined as boolean | undefined,
  listingAgeRange: '' as string,
  minPrice: undefined as number | undefined,
  maxPrice: undefined as number | undefined,
  minFlashPrice: undefined as number | undefined,
  maxFlashPrice: undefined as number | undefined,
  minInventory: undefined as number | undefined,
  maxInventory: undefined as number | undefined,
  minCumulativeGmv: undefined as number | undefined,
  maxCumulativeGmv: undefined as number | undefined,
  minOrderCount: undefined as number | undefined,
  maxOrderCount: undefined as number | undefined,
  minUnitsSold: undefined as number | undefined,
  maxUnitsSold: undefined as number | undefined,
  sellerType: '',
  currency: '',
  sortField: 'updateTime',
  sortOrder: 'DESC',
  pageNum: 1,
  pageSize: 20,
});

const statusOptions = [
  { label: '在售中', value: 'ACTIVATE' },
  { label: '已下架', value: 'SELLER_DEACTIVATED' },
  { label: '平台下架', value: 'PLATFORM_DEACTIVATED' },
  { label: '审核中', value: 'PENDING' },
  { label: '已删除', value: 'DELETED' },
];

const categoryCascaderProps: CascaderProps = {
  checkStrictly: false,
  emitPath: false,
  lazy: true,
  lazyLoad: loadCategoryChildren,
  multiple: true,
};

const authorizationIssueShops = computed(() =>
  shops.value.filter((shop) => {
    const capability = capabilityMap.value[shop.shopId];
    return Boolean(
      capability?.reauthorizationRequired ||
      capability?.promotionAccessStatus === 'REAUTH_REQUIRED' ||
      rows.value.some(
        (row) =>
          row.shopId === shop.shopId && Boolean(row.reauthorizationRequired),
      ),
    );
  }),
);
const preparingShops = computed(() => {
  const selected = new Set(query.shopIds);
  return shops.value.filter((shop) => {
    if (selected.size > 0 && !selected.has(shop.shopId)) return false;
    return !['BASE_READY', 'LEGACY_READY', 'READY'].includes(
      shop.initializationStatus || 'LEGACY_READY',
    );
  });
});

const selectedProductCount = computed(() => selectedRows.value.length);
const shopSyncProgress = computed(() =>
  aggregateProductSyncByShop(syncStates.value),
);

function shopName(shop: AuthorizedShopOption) {
  return shop.shopAlias || shop.tiktokShopName;
}

function money(min: any, max: any, currency = 'USD') {
  if (min === undefined || min === null || min === '') return '-';
  const left = Number(min).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  const right = Number(max ?? min).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  return `${currency || 'USD'} ${left}${String(min) === String(max ?? min) ? '' : ` ~ ${right}`}`;
}

function dateTime(value: any, epoch = false) {
  if (!value) return '-';
  const date = epoch ? new Date(Number(value) * 1000) : new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

function statusLabel(value: string) {
  return (
    statusOptions.find((item) => item.value === value)?.label || value || '-'
  );
}

function statusType(value: string) {
  if (value === 'ACTIVATE') return 'success';
  if (value === 'PENDING') return 'warning';
  return 'info';
}

function categoryNode(
  row: ProductManagementCategory,
  level: 1 | 2 | 3,
): CategoryCascaderOption {
  const labels = [
    row.categoryNameLevel1,
    row.categoryNameLevel2,
    row.categoryNameLevel3,
  ];
  const chinese = labels[level - 1] || row.categoryId;
  const english =
    (row as Dict)[`categoryNameEnLevel${level}`] ||
    (row as Dict)[`categoryNameLevel${level}En`];
  return {
    label: english ? `${chinese} / ${english}` : chinese,
    leaf: row.leaf ?? level === 3,
    value: row.categoryId,
  };
}

function categoryCacheKey(level: 1 | 2 | 3, parentCategoryId?: string) {
  return `${level}:${parentCategoryId || ''}`;
}

function readCategoryCache(key: string) {
  const memory = categoryMemoryCache.get(key);
  if (memory?.expiresAt && memory.expiresAt > Date.now()) return memory.rows;
  try {
    const raw = window.sessionStorage.getItem(`${CATEGORY_CACHE_PREFIX}${key}`);
    const cached = raw ? (JSON.parse(raw) as CategoryCacheEntry) : undefined;
    if (cached?.expiresAt && cached.expiresAt > Date.now()) {
      categoryMemoryCache.set(key, cached);
      return cached.rows;
    }
  } catch {
    // 隐私模式或存储配额异常时直接回源，不影响类目选择。
  }
}

function writeCategoryCache(key: string, rows: ProductManagementCategory[]) {
  const cached: CategoryCacheEntry = {
    expiresAt: Date.now() + CATEGORY_CACHE_TTL_MS,
    rows,
  };
  categoryMemoryCache.set(key, cached);
  try {
    window.sessionStorage.setItem(
      `${CATEGORY_CACHE_PREFIX}${key}`,
      JSON.stringify(cached),
    );
  } catch {
    // 内存缓存仍然生效。
  }
}

async function fetchCategoryNodes(level: 1 | 2 | 3, parentCategoryId?: string) {
  const key = categoryCacheKey(level, parentCategoryId);
  const cached = readCategoryCache(key);
  const rows =
    cached ||
    (await getProductManagementCategoriesApi({ level, parentCategoryId }));
  if (!cached) writeCategoryCache(key, rows);
  return rows
    .map((row) => categoryNode(row, level))
    .sort((left, right) => left.label.localeCompare(right.label, 'zh-CN'));
}

async function loadCategoryChildren(
  node: CascaderNode,
  resolve: (options?: CascaderOption[]) => void,
) {
  const level = (node.level + 1) as 1 | 2 | 3;
  if (level > 3 || !node.value) return resolve([]);
  try {
    resolve(await fetchCategoryNodes(level, String(node.value)));
  } catch {
    resolve([]);
  }
}

async function loadCategoryOptions() {
  categoryLoading.value = true;
  try {
    categoryTreeOptions.value = await fetchCategoryNodes(1);
  } finally {
    categoryLoading.value = false;
  }
}

async function loadCapabilities() {
  const capabilities: Record<number, OperationsCapability> = {};
  for (let offset = 0; offset < shops.value.length; offset += 4) {
    const batch = shops.value.slice(offset, offset + 4);
    const results = await Promise.allSettled(
      batch.map(async (shop) => ({
        capability: await getOperationsCapabilityApi(shop.shopId),
        shopId: shop.shopId,
      })),
    );
    results.forEach((result) => {
      if (result.status === 'fulfilled')
        capabilities[result.value.shopId] = result.value.capability;
    });
    capabilityMap.value = { ...capabilities };
  }
}

function goToUpdateAuthorization(shop = authorizationIssueShops.value[0]) {
  if (!shop) {
    void router.push('/market/shopAuth');
    return;
  }
  void router.push({
    path: '/market/shopAuth',
    query: {
      action: 'update',
      shopId: String(shop.shopId),
      shopName: shopName(shop),
      shopType: shop.sellerType === 'LOCAL' ? 'US_LOCAL' : 'CROSS_BORDER',
    },
  });
}

function detailShop() {
  return shops.value.find((shop) => shop.shopId === detail.value?.shopId);
}

async function loadShops() {
  const result = (await getShopListApi({
    authStatus: 1,
    pageNum: 1,
    pageSize: 100,
  })) as { rows?: AuthorizedShopOption[] };
  shops.value = (result.rows || []).filter((item) => item.authStatus === 1);
  void loadCapabilities();
  void loadCategoryOptions();
}

async function search(resetPage = false) {
  if (resetPage) query.pageNum = 1;
  const sequence = ++searchSequence;
  loading.value = true;
  try {
    const result = await searchProductManagementApi({ ...query });
    if (sequence !== searchSequence) return;
    rows.value = result.rows || [];
    total.value = Number(result.total || 0);
  } finally {
    if (sequence === searchSequence) loading.value = false;
  }
}

function handleCurrentPageChange(page: number) {
  query.pageNum = page;
  void search();
}

function handlePageSizeChange(pageSize: number) {
  query.pageSize = pageSize;
  query.pageNum = 1;
  void search();
}

function reset() {
  Object.assign(query, {
    shopIds: [],
    keyword: '',
    statuses: ['ACTIVATE'],
    categoryKeyword: '',
    categoryIds: [],
    flashSaleActive: undefined,
    listingAgeRange: '',
    minPrice: undefined,
    maxPrice: undefined,
    minFlashPrice: undefined,
    maxFlashPrice: undefined,
    minInventory: undefined,
    maxInventory: undefined,
    minCumulativeGmv: undefined,
    maxCumulativeGmv: undefined,
    minOrderCount: undefined,
    maxOrderCount: undefined,
    minUnitsSold: undefined,
    maxUnitsSold: undefined,
    sellerType: '',
    currency: '',
    sortField: 'updateTime',
    sortOrder: 'DESC',
    pageNum: 1,
  });
  void search();
}

async function openDetail(row: Dict) {
  clearDetailRefreshTimer();
  clearImageCacheTimer();
  detailVisible.value = true;
  detailLoading.value = true;
  detailTab.value = 'basic';
  detailRefreshExhausted.value = false;
  detailRefreshAttempts = 0;
  imageCacheAttempts.value = 0;
  try {
    detail.value = await getProductManagementDetailApi({
      productId: row.productId,
      shopId: row.shopId,
    });
    scheduleDetailRefresh();
    if (detailTab.value === 'images') void loadDetailImages();
  } finally {
    detailLoading.value = false;
  }
}

function clearDetailRefreshTimer() {
  if (!detailRefreshTimer) return;
  window.clearTimeout(detailRefreshTimer);
  detailRefreshTimer = undefined;
}

function scheduleDetailRefresh() {
  clearDetailRefreshTimer();
  if (detailRefreshAttempts >= DETAIL_REFRESH_MAX_ATTEMPTS) {
    detailRefreshExhausted.value = true;
    return;
  }
  if (!detailVisible.value || detail.value?.detailDataStatus !== 'PENDING')
    return;
  detailRefreshTimer = window.setTimeout(() => {
    detailRefreshTimer = undefined;
    void refreshIncompleteDetail();
  }, 2000);
}

async function refreshIncompleteDetail() {
  if (!detailVisible.value || !detail.value) return;
  const productId = detail.value.productId;
  const shopId = detail.value.shopId;
  detailRefreshAttempts += 1;
  try {
    const refreshed = await getProductManagementDetailApi({
      productId,
      shopId,
    });
    if (
      !detailVisible.value ||
      detail.value?.productId !== productId ||
      detail.value?.shopId !== shopId
    )
      return;
    detail.value = refreshed;
    if (detailTab.value === 'images') void loadDetailImages();
    if (refreshed.detailDataStatus !== 'PENDING') {
      return;
    }
  } catch {
    // 后台补齐短暂失败时保留已有快照，下一轮继续读取本地结果。
  }
  scheduleDetailRefresh();
}

function retryIncompleteDetail() {
  detailRefreshAttempts = 0;
  detailRefreshExhausted.value = false;
  void refreshIncompleteDetail();
}

const readyImageUrls = computed(() =>
  (detail.value?.images || []).map((item: Dict) => item.url).filter(Boolean),
);
const hasFailedProductImages = computed(() =>
  (detail.value?.images || []).some(
    (image: Dict) =>
      image.imageType === 'GALLERY' && image.cacheStatus === 'FAILED',
  ),
);

function clearImageCacheTimer() {
  if (!imageCacheTimer) return;
  window.clearTimeout(imageCacheTimer);
  imageCacheTimer = undefined;
}

function imageCacheLabel(image: Dict) {
  if (image.cacheStatus === 'FAILED') return '图片暂时无法加载';
  return '图片加载中';
}

async function loadDetailImages(includeFailed = false) {
  if (
    imageCacheLoading.value ||
    !detailVisible.value ||
    detailTab.value !== 'images'
  )
    return;
  const targets = (detail.value?.images || []).filter(
    (item: Dict) =>
      item.imageType === 'GALLERY' &&
      !item.url &&
      (includeFailed || item.cacheStatus !== 'FAILED'),
  );
  if (targets.length === 0) return;
  imageCacheLoading.value = true;
  try {
    for (let index = 0; index < targets.length; index += 50) {
      const batch = targets.slice(index, index + 50);
      const result = await cacheProductManagementImagesApi({
        imageIds: batch.map((item: Dict) => Number(item.id)),
      });
      const updates = new Map(
        (result.images || []).map((item) => [Number(item.imageId), item]),
      );
      for (const image of detail.value?.images || []) {
        const update = updates.get(Number(image.id));
        if (!update) continue;
        image.cacheStatus = update.status;
        image.url = update.url;
        image.cacheExpiresAt = update.cacheExpiresAt;
      }
    }
    imageCacheAttempts.value += 1;
    const pending = (detail.value?.images || []).some(
      (item: Dict) =>
        item.imageType === 'GALLERY' &&
        !item.url &&
        item.cacheStatus !== 'FAILED',
    );
    if (pending && imageCacheAttempts.value < 15) {
      clearImageCacheTimer();
      imageCacheTimer = window.setTimeout(() => {
        imageCacheTimer = undefined;
        void loadDetailImages();
      }, 2000);
    }
  } catch {
    imageCacheAttempts.value += 1;
    if (imageCacheAttempts.value < 5 && detailVisible.value) {
      clearImageCacheTimer();
      imageCacheTimer = window.setTimeout(() => {
        imageCacheTimer = undefined;
        void loadDetailImages();
      }, 3000);
    }
  } finally {
    imageCacheLoading.value = false;
  }
}

function retryDetailImages() {
  imageCacheAttempts.value = 0;
  void loadDetailImages(true);
}

const sanitizedDescription = computed(() => {
  const html = sanitizeProductDescription(detail.value?.descriptionHtml);
  if (!html) return '';
  const document = new DOMParser().parseFromString(html, 'text/html');
  const descriptionImages = (detail.value?.images || [])
    .filter((image: Dict) => image.imageType === 'DESCRIPTION')
    .sort(
      (left: Dict, right: Dict) => Number(left.sortNo) - Number(right.sortNo),
    );
  document.querySelectorAll('img').forEach((element, index) => {
    const cachedUrl = descriptionImages[index]?.url;
    element.removeAttribute('srcset');
    delete element.dataset.src;
    if (cachedUrl) element.setAttribute('src', cachedUrl);
    else element.removeAttribute('src');
  });
  return document.body.innerHTML;
});

function skuFlashPrice(skuId: string) {
  return (detail.value?.activePromotions || []).find(
    (promotion: Dict) => promotion.skuId === skuId,
  );
}

function inventoryText(inventory: Dict[]) {
  if (!Array.isArray(inventory) || inventory.length === 0) return '-';
  return inventory
    .map(
      (item) =>
        `${item.warehouse_name || item.warehouse_id || '仓库'}: ${Number(item.quantity || 0).toLocaleString()}`,
    )
    .join(' / ');
}

function flashSaleState(row: Dict) {
  if (row.flashSaleDataStatus === 'AVAILABLE' && row.minFlashPrice !== null)
    return money(row.minFlashPrice, row.maxFlashPrice, row.flashCurrency);
  const labels: Record<string, string> = {
    CONFIRMED_EMPTY: '暂无生效活动',
    PENDING: '营销资料待同步',
    REAUTH_REQUIRED: '需重新授权',
    SYNC_FAILED: '营销资料同步失败',
  };
  return labels[row.flashSaleDataStatus] || '营销资料待同步';
}

function flashSaleStateType(row: Dict) {
  if (row.flashSaleDataStatus === 'REAUTH_REQUIRED') return 'danger';
  if (['PENDING', 'SYNC_FAILED'].includes(row.flashSaleDataStatus))
    return 'warning';
  return 'info';
}

function shopTimezone(region?: string) {
  return String(region || '').toUpperCase() === 'US'
    ? { label: 'America/Los_Angeles (PT)', value: 'America/Los_Angeles' }
    : { label: 'UTC', value: 'UTC' };
}

function zonedDateTime(value: any, region?: string) {
  if (!value) return '-';
  const date = new Date(Number(value) * 1000);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('zh-CN', {
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone: shopTimezone(region).value,
    year: 'numeric',
  }).format(date);
}

function selectedByShop() {
  const grouped = new Map<number, Dict[]>();
  selectedRows.value.forEach((row) => {
    grouped.set(row.shopId, [...(grouped.get(row.shopId) || []), row]);
  });
  return grouped;
}

function batchIneligibleReason(command: string, row: Dict) {
  const capability = capabilityMap.value[row.shopId];
  if (!capability) return '店铺权限校验中，请稍后重试';
  if (!capability.productReadable) return '当前店铺缺少商品权限，请重新授权';
  if (command === 'activate') {
    if (!capability?.productActivateEnabled) return '当前环境未开放上架能力';
    if (row.status !== 'SELLER_DEACTIVATED') return '仅已下架商品可上架';
  }
  if (command === 'deactivate') {
    if (!capability?.productDeactivateEnabled) return '当前环境未开放下架能力';
    if (row.status !== 'ACTIVATE') return '仅在售商品可下架';
  }
  if (command === 'delete') {
    if (!capability?.productDeleteEnabled) return '当前授权缺少商品删除能力';
    if (!capability.productDeleteAuthorized)
      return '当前店铺缺少商品删除权限，请重新授权';
    if (row.status === 'DELETED') return '商品已删除';
  }
  return undefined;
}

async function runBatchOperation(
  command: 'activate' | 'deactivate' | 'delete',
) {
  if (selectedRows.value.length === 0) {
    ElMessage.warning('请先勾选商品');
    return;
  }
  const ineligible = selectedRows.value.find((row) =>
    batchIneligibleReason(command, row),
  );
  if (ineligible) {
    const reason = batchIneligibleReason(command, ineligible);
    if (reason?.includes('重新授权')) {
      try {
        await ElMessageBox.confirm(
          `商品 ${ineligible.productId} 不可执行该操作：${reason}`,
          '店铺权限不足',
          {
            cancelButtonText: '暂不处理',
            confirmButtonText: '去重新授权',
            type: 'warning',
          },
        );
        goToUpdateAuthorization(
          shops.value.find((shop) => shop.shopId === ineligible.shopId),
        );
      } catch {
        // 用户保留当前选择，稍后仍可从页面授权提示进入更新授权。
      }
    } else {
      ElMessage.warning(
        `商品 ${ineligible.productId} 不可执行该操作：${reason}`,
      );
    }
    return;
  }
  const label = { activate: '上架', deactivate: '下架', delete: '删除' }[
    command
  ];
  await ElMessageBox.confirm(
    command === 'delete'
      ? `确认删除已选 ${selectedRows.value.length} 个商品？系统会先自动下架，删除后不可恢复。`
      : `确认${label}已选 ${selectedRows.value.length} 个商品？`,
    `批量${label}`,
    { type: command === 'delete' ? 'warning' : 'info' },
  );
  const taskType = {
    activate: 'ACTIVATE_SELECTED',
    deactivate: 'DEACTIVATE_SELECTED',
    delete: 'DELETE_SELECTED',
  }[command];
  const settled = await Promise.allSettled(
    [...selectedByShop()].map(([shopId, products]) =>
      createProductOperationTaskApi({
        productIds: products.map((row) => String(row.productId)),
        productLevel: 'PRODUCT',
        shopId,
        taskType,
      }),
    ),
  );
  const accepted = settled.filter((item) => item.status === 'fulfilled').length;
  const failed = settled.length - accepted;
  if (accepted > 0)
    ElMessage.success(`已提交 ${accepted} 个店铺的${label}任务`);
  if (failed > 0) ElMessage.warning(`${failed} 个店铺任务提交失败`);
  window.setTimeout(() => void search(), 2500);
}

async function refreshStatus() {
  if (statusRefreshing.value) return;
  const wasSyncing = syncing.value;
  statusRefreshing.value = true;
  try {
    syncStates.value = await getProductManagementSyncStatusApi({
      shopIds: query.shopIds,
    });
    syncing.value = syncStates.value.some((item) =>
      ['PENDING', 'RUNNING'].includes(item.status),
    );
    if (!syncing.value) syncTaskVisible.value = false;
    if (!syncing.value && wasSyncing) await search();
  } catch {
    // 状态轮询短暂失败时保留当前页面和同步态，下一轮自动重试。
  } finally {
    statusRefreshing.value = false;
    if (
      !disposed &&
      (syncing.value || syncTaskVisible.value) &&
      document.visibilityState === 'visible' &&
      !syncTimer
    ) {
      syncTimer = window.setTimeout(
        () => {
          syncTimer = undefined;
          void refreshStatus();
        },
        syncing.value ? 3000 : 10_000,
      );
    }
  }
}

async function syncNow() {
  syncSubmitting.value = true;
  try {
    const result = await syncProductManagementApi({
      shopIds: query.shopIds,
      syncType: 'ALL',
    });
    const accepted = Number(result.accepted || 0);
    const skipped = Number(result.skippedRecent || 0);
    if (accepted > 0) ElMessage.success(`已创建 ${accepted} 个店铺同步任务`);
    else if (skipped > 0) ElMessage.info('10 分钟内已同步过，无需重复提交');
    else ElMessage.info(result.message || '店铺已有同步任务正在执行');
    syncTaskVisible.value = true;
    await refreshStatus();
  } finally {
    syncSubmitting.value = false;
  }
}

function changeSort({ order, prop }: { order: null | string; prop: string }) {
  if (!order) return;
  query.sortField = prop;
  query.sortOrder = order === 'ascending' ? 'ASC' : 'DESC';
  void search(true);
}

onMounted(async () => {
  await loadShops();
  await Promise.all([search(), refreshStatus()]);
});

onBeforeUnmount(() => {
  disposed = true;
  if (syncTimer) window.clearTimeout(syncTimer);
  clearDetailRefreshTimer();
  clearImageCacheTimer();
});

watch(detailVisible, (visible) => {
  if (!visible) {
    clearDetailRefreshTimer();
    clearImageCacheTimer();
  }
});

watch(detailTab, (tab) => {
  if (tab === 'images') void loadDetailImages();
  else clearImageCacheTimer();
});

watch(syncTaskVisible, (visible) => {
  if (visible) void refreshStatus();
  else if (!syncing.value && syncTimer) {
    window.clearTimeout(syncTimer);
    syncTimer = undefined;
  }
});
</script>

<template>
  <div class="product-management">
    <ElCard shadow="never" class="filter-card">
      <div class="page-heading">
        <div>
          <h2>TikTok 商品管理</h2>
          <p>默认展示全部授权店铺的在售商品；页面数据来自本地数据库快照。</p>
        </div>
        <div class="sync-area">
          <div class="sync-buttons">
            <ElButton :loading="syncSubmitting" type="primary" @click="syncNow">
              同步商品数据
            </ElButton>
            <ElButton
              v-if="syncing"
              link
              type="primary"
              @click="syncTaskVisible = true"
            >
              查看同步进度
            </ElButton>
          </div>
        </div>
      </div>

      <ElAlert
        v-if="preparingShops.length > 0"
        :closable="false"
        show-icon
        type="info"
        :title="`${preparingShops.map(shopName).join('、')} 正在初始化商品数据`"
        description="目录完成前不会把空列表显示为 0 个商品；达到“列表已可用”后会先开放商品列表，再继续补齐 SKU、详情与图片。"
        class="authorization-alert"
      />

      <ElAlert
        v-if="authorizationIssueShops.length > 0"
        :closable="false"
        show-icon
        type="warning"
        :title="`${authorizationIssueShops.map(shopName).join('、')} 缺少商品营销资料权限，秒杀价无法判定`"
        class="authorization-alert"
      >
        <template #default>
          <ElButton link type="primary" @click="goToUpdateAuthorization()">
            重新授权
          </ElButton>
        </template>
      </ElAlert>

      <div class="filters">
        <ElSelect
          v-model="query.shopIds"
          clearable
          collapse-tags
          multiple
          placeholder="全部店铺"
        >
          <ElOption
            v-for="shop in shops"
            :key="shop.shopId"
            :label="shopName(shop)"
            :value="shop.shopId"
          />
        </ElSelect>
        <ElInput
          v-model="query.keyword"
          clearable
          placeholder="商品标题 / 商品ID / 商家SKU"
          @keyup.enter="search(true)"
        />
        <ElSelect
          v-model="query.statuses"
          clearable
          collapse-tags
          multiple
          placeholder="商品状态"
        >
          <ElOption
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </ElSelect>
        <ElCascader
          v-model="query.categoryIds"
          :loading="categoryLoading"
          :options="categoryTreeOptions"
          :props="categoryCascaderProps"
          clearable
          collapse-tags
          filterable
          placeholder="商品类目（中/英文）"
        />
        <ElSelect
          v-model="query.listingAgeRange"
          clearable
          placeholder="上架时长"
        >
          <ElOption label="7 天以内" value="D0_7" />
          <ElOption label="8 - 15 天" value="D8_15" />
          <ElOption label="16 - 30 天" value="D16_30" />
          <ElOption label="30 天以上" value="D30_PLUS" />
        </ElSelect>
        <ElSelect
          v-model="query.flashSaleActive"
          clearable
          placeholder="生效秒杀"
        >
          <ElOption label="有生效秒杀" :value="true" />
          <ElOption label="无生效秒杀" :value="false" />
        </ElSelect>
        <ElButton type="primary" @click="search(true)">查询</ElButton>
        <ElButton @click="reset">重置</ElButton>
        <ElButton link type="primary" @click="advanced = !advanced">
          {{ advanced ? '收起筛选' : '更多筛选' }}
        </ElButton>
      </div>

      <div v-if="advanced" class="advanced-filters">
        <ElSelect v-model="query.sellerType" clearable placeholder="店铺类型">
          <ElOption label="美国本土店" value="LOCAL" />
          <ElOption label="跨境 POP 店" value="CROSS_BORDER" />
        </ElSelect>
        <ElInput
          v-model="query.currency"
          clearable
          placeholder="币种，如 USD"
        />
        <div class="range">
          <span>售价</span
          ><ElInputNumber
            v-model="query.minPrice"
            :controls="false"
            placeholder="最低"
          /><i>-</i
          ><ElInputNumber
            v-model="query.maxPrice"
            :controls="false"
            placeholder="最高"
          />
        </div>
        <div class="range">
          <span>秒杀价</span
          ><ElInputNumber
            v-model="query.minFlashPrice"
            :controls="false"
            placeholder="最低"
          /><i>-</i
          ><ElInputNumber
            v-model="query.maxFlashPrice"
            :controls="false"
            placeholder="最高"
          />
        </div>
        <div class="range">
          <span>库存</span
          ><ElInputNumber
            v-model="query.minInventory"
            :controls="false"
            placeholder="最低"
          /><i>-</i
          ><ElInputNumber
            v-model="query.maxInventory"
            :controls="false"
            placeholder="最高"
          />
        </div>
        <div class="range">
          <span>累计 GMV</span
          ><ElInputNumber
            v-model="query.minCumulativeGmv"
            :controls="false"
            placeholder="最低"
          /><i>-</i
          ><ElInputNumber
            v-model="query.maxCumulativeGmv"
            :controls="false"
            placeholder="最高"
          />
        </div>
        <div class="range">
          <span>订单数</span
          ><ElInputNumber
            v-model="query.minOrderCount"
            :controls="false"
            placeholder="最低"
          /><i>-</i
          ><ElInputNumber
            v-model="query.maxOrderCount"
            :controls="false"
            placeholder="最高"
          />
        </div>
        <div class="range">
          <span>成交件数</span
          ><ElInputNumber
            v-model="query.minUnitsSold"
            :controls="false"
            placeholder="最低"
          /><i>-</i
          ><ElInputNumber
            v-model="query.maxUnitsSold"
            :controls="false"
            placeholder="最高"
          />
        </div>
      </div>
    </ElCard>

    <ElCard shadow="never" class="table-card">
      <div class="batch-toolbar">
        <span>已选 {{ selectedProductCount }} 个商品</span>
        <ElDropdown trigger="click" @command="runBatchOperation">
          <ElButton :disabled="selectedProductCount === 0"> 批量操作 </ElButton>
          <template #dropdown>
            <ElDropdownMenu>
              <ElDropdownItem command="activate">上架</ElDropdownItem>
              <ElDropdownItem command="deactivate">下架</ElDropdownItem>
              <ElDropdownItem command="delete" divided>删除</ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
        <span v-if="loading && rows.length > 0" class="refreshing-tip">
          正在更新列表…
        </span>
      </div>
      <ElTable
        v-loading="loading && rows.length === 0"
        :data="rows"
        row-key="id"
        @selection-change="selectedRows = $event"
        @sort-change="changeSort"
      >
        <ElTableColumn type="selection" fixed width="48" />
        <ElTableColumn label="商品" fixed min-width="300">
          <template #default="{ row }">
            <div class="product-cell">
              <ElImage :src="row.imageUrl" fit="cover" class="product-image">
                <template #error>
                  <div class="image-placeholder">暂无图片</div>
                </template>
              </ElImage>
              <div class="product-copy">
                <ElTooltip :content="row.title" placement="top-start">
                  <button class="title-link" @click="openDetail(row)">
                    {{ row.title }}
                  </button>
                </ElTooltip>
                <span>ID：{{ row.productId }}</span>
              </div>
            </div>
          </template>
        </ElTableColumn>
        <ElTableColumn label="店铺" min-width="150">
          <template #default="{ row }">
            <div>{{ row.shopName }}</div>
            <small>{{ row.sellerType || '-' }}</small>
          </template>
        </ElTableColumn>
        <ElTableColumn label="状态" width="110">
          <template #default="{ row }">
            <ElTag :type="statusType(row.status)">
              {{ statusLabel(row.status) }}
            </ElTag>
          </template>
        </ElTableColumn>
        <ElTableColumn label="类目" min-width="220">
          <template #default="{ row }">
            <ElTooltip
              :content="
                [row.categoryPath, row.categoryPathEn]
                  .filter(Boolean)
                  .join(' / ') || '资料同步中'
              "
              placement="top-start"
            >
              <div class="category-nowrap">
                <span>{{ row.categoryLevel1Name || '资料同步中' }}</span>
              </div>
            </ElTooltip>
          </template>
        </ElTableColumn>
        <ElTableColumn
          label="商品售价"
          prop="price"
          sortable="custom"
          min-width="145"
        >
          <template #default="{ row }">
            {{ money(row.minPrice, row.maxPrice, row.currency) }}
          </template>
        </ElTableColumn>
        <ElTableColumn label="生效秒杀价" min-width="145">
          <template #default="{ row }">
            <span
              v-if="row.flashSaleDataStatus === 'AVAILABLE'"
              class="flash-price"
            >
              {{ flashSaleState(row) }}
            </span>
            <ElButton
              v-else-if="row.flashSaleDataStatus === 'REAUTH_REQUIRED'"
              link
              type="danger"
              @click="
                goToUpdateAuthorization(
                  shops.find((shop) => shop.shopId === row.shopId),
                )
              "
            >
              {{ flashSaleState(row) }}
            </ElButton>
            <ElTag v-else :type="flashSaleStateType(row)" effect="plain">
              {{ flashSaleState(row) }}
            </ElTag>
          </template>
        </ElTableColumn>
        <ElTableColumn
          label="库存"
          prop="inventory"
          sortable="custom"
          width="105"
        >
          <template #default="{ row }">
            {{ Number(row.inventoryTotal || 0).toLocaleString() }}
          </template>
        </ElTableColumn>
        <ElTableColumn label="SKU 数" width="90" prop="skuCount" />
        <ElTableColumn
          label="累计 GMV"
          prop="cumulativeGmv"
          sortable="custom"
          min-width="130"
        >
          <template #default="{ row }">
            {{
              money(
                row.cumulativeGmv || 0,
                row.cumulativeGmv || 0,
                row.salesCurrency || row.currency,
              )
            }}
          </template>
        </ElTableColumn>
        <ElTableColumn
          label="订单数"
          prop="orderCount"
          sortable="custom"
          width="110"
        />
        <ElTableColumn
          label="成交件数"
          prop="unitsSold"
          sortable="custom"
          width="120"
        />
        <ElTableColumn
          label="上架时间"
          prop="createTime"
          sortable="custom"
          min-width="165"
        >
          <template #default="{ row }">
            {{ dateTime(row.createTime, true) }}
          </template>
        </ElTableColumn>
        <ElTableColumn
          label="平台更新时间"
          prop="updateTime"
          sortable="custom"
          min-width="165"
        >
          <template #default="{ row }">
            {{ dateTime(row.updateTime, true) }}
          </template>
        </ElTableColumn>
        <ElTableColumn label="操作" fixed="right" width="90">
          <template #default="{ row }">
            <ElButton link type="primary" @click="openDetail(row)">
              详情
            </ElButton>
          </template>
        </ElTableColumn>
        <template #empty>
          <ElEmpty
            :description="
              preparingShops.length > 0
                ? '店铺商品正在初始化，当前空列表不代表 0 个商品'
                : '已完成数据准备，暂无符合条件的商品'
            "
          />
        </template>
      </ElTable>
      <div class="pagination">
        <ElPagination
          v-model:current-page="query.pageNum"
          v-model:page-size="query.pageSize"
          :page-sizes="[20, 50, 100]"
          :total="total"
          background
          layout="total, sizes, prev, pager, next, jumper"
          @current-change="handleCurrentPageChange"
          @size-change="handlePageSizeChange"
        />
      </div>
    </ElCard>

    <ElDrawer v-model="detailVisible" size="76%" title="商品详情">
      <div v-loading="detailLoading" class="detail-body">
        <template v-if="detail">
          <ElAlert
            v-if="detail.detailDataStatus === 'PENDING'"
            :closable="false"
            show-icon
            :title="
              detailRefreshExhausted
                ? '商品资料暂未补齐，可稍后重试'
                : '商品基础资料正在后台补齐，当前先展示已同步内容'
            "
            type="info"
            class="detail-alert"
          >
            <template v-if="detailRefreshExhausted" #default>
              <ElButton link type="primary" @click="retryIncompleteDetail">
                重试补齐
              </ElButton>
            </template>
          </ElAlert>
          <div class="detail-heading">
            <ElImage :src="detail.imageUrl" fit="cover" class="detail-image">
              <template #error>
                <div class="image-placeholder">暂无图片</div>
              </template>
            </ElImage>
            <div>
              <h3>{{ detail.title }}</h3>
              <p>{{ detail.shopName }} · {{ detail.productId }}</p>
              <ElTag :type="statusType(detail.status)">
                {{ statusLabel(detail.status) }}
              </ElTag>
            </div>
          </div>
          <ElTabs v-model="detailTab">
            <ElTabPane label="基础资料" name="basic">
              <ElDescriptions :column="3" border>
                <ElDescriptionsItem label="类目">
                  <ElTooltip
                    :content="
                      [detail.categoryPath, detail.categoryPathEn]
                        .filter(Boolean)
                        .join(' / ') || '资料同步中'
                    "
                    placement="top-start"
                  >
                    <div class="category-nowrap detail-category">
                      {{ detail.categoryPath || '资料同步中' }}
                      <small v-if="detail.categoryPathEn">
                        / {{ detail.categoryPathEn }}
                      </small>
                    </div>
                  </ElTooltip>
                </ElDescriptionsItem>
                <ElDescriptionsItem label="品牌">
                  {{ detail.brandName || '-' }}
                </ElDescriptionsItem>
                <ElDescriptionsItem label="商品售价">
                  {{ money(detail.minPrice, detail.maxPrice, detail.currency) }}
                </ElDescriptionsItem>
                <ElDescriptionsItem label="库存">
                  {{ detail.inventoryTotal || 0 }}
                </ElDescriptionsItem>
                <ElDescriptionsItem label="上架时间">
                  {{ dateTime(detail.createTime, true) }}
                </ElDescriptionsItem>
                <ElDescriptionsItem label="更新时间">
                  {{ dateTime(detail.updateTime, true) }}
                </ElDescriptionsItem>
              </ElDescriptions>
            </ElTabPane>
            <ElTabPane label="SKU 与库存" name="sku">
              <ElTable :data="detail.skus || []">
                <ElTableColumn
                  label="商家 SKU"
                  prop="sellerSku"
                  min-width="160"
                />
                <ElTableColumn label="平台 SKU" prop="skuId" min-width="180" />
                <ElTableColumn label="规格" prop="specText" min-width="220" />
                <ElTableColumn label="售价" min-width="130">
                  <template #default="{ row }">
                    {{ money(row.salePrice, row.salePrice, row.currency) }}
                  </template>
                </ElTableColumn>
                <ElTableColumn label="划线价" min-width="130">
                  <template #default="{ row }">
                    {{ money(row.listPrice, row.listPrice, row.currency) }}
                  </template>
                </ElTableColumn>
                <ElTableColumn label="生效秒杀价" min-width="140">
                  <template #default="{ row }">
                    <span v-if="skuFlashPrice(row.skuId)" class="flash-price">
                      {{
                        money(
                          skuFlashPrice(row.skuId).activityPrice,
                          skuFlashPrice(row.skuId).activityPrice,
                          skuFlashPrice(row.skuId).currency,
                        )
                      }}
                    </span>
                    <ElButton
                      v-else-if="
                        detail.flashSaleDataStatus === 'REAUTH_REQUIRED'
                      "
                      link
                      type="danger"
                      @click="goToUpdateAuthorization(detailShop())"
                    >
                      需重新授权
                    </ElButton>
                    <ElTag
                      v-else-if="detail.flashSaleDataStatus !== 'AVAILABLE'"
                      :type="flashSaleStateType(detail)"
                      effect="plain"
                    >
                      {{ flashSaleState(detail) }}
                    </ElTag>
                    <span v-else>暂无生效活动</span>
                  </template>
                </ElTableColumn>
                <ElTableColumn label="库存" prop="inventoryTotal" width="100" />
                <ElTableColumn label="仓库库存" min-width="220">
                  <template #default="{ row }">
                    {{ inventoryText(row.inventory) }}
                  </template>
                </ElTableColumn>
                <ElTableColumn label="SKU 状态" min-width="110">
                  <template #default="{ row }">
                    {{ row.status || '正常' }}
                  </template>
                </ElTableColumn>
                <ElTableColumn label="资料更新时间" min-width="170">
                  <template #default="{ row }">
                    {{ dateTime(row.syncedAt) }}
                  </template>
                </ElTableColumn>
              </ElTable>
            </ElTabPane>
            <ElTabPane label="商品图片" name="images">
              <div v-if="detail.images?.length" class="image-grid">
                <div v-for="image in detail.images" :key="image.id">
                  <ElImage
                    v-if="image.url"
                    :src="image.url"
                    :preview-src-list="readyImageUrls"
                    fit="cover"
                  />
                  <div v-else class="image-cache-placeholder">
                    <span>{{ imageCacheLabel(image) }}</span>
                  </div>
                </div>
              </div>
              <ElEmpty v-else description="商品图片待同步" />
              <div v-if="hasFailedProductImages" class="image-cache-actions">
                <ElButton
                  :loading="imageCacheLoading"
                  link
                  type="primary"
                  @click="retryDetailImages"
                >
                  重新加载图片
                </ElButton>
              </div>
            </ElTabPane>
            <ElTabPane label="详情介绍" name="description">
              <div
                v-if="sanitizedDescription"
                class="product-description"
                v-html="sanitizedDescription"
              ></div>
              <ElEmpty v-else description="详情资料待同步" />
            </ElTabPane>
            <ElTabPane label="累计销售" name="sales">
              <div class="metric-grid">
                <div>
                  <span>累计 GMV</span
                  ><strong>{{
                    money(
                      detail.cumulativeGmv || 0,
                      detail.cumulativeGmv || 0,
                      detail.salesCurrency || detail.currency,
                    )
                  }}</strong>
                </div>
                <div>
                  <span>订单数</span
                  ><strong>{{
                    Number(detail.orderCount || 0).toLocaleString()
                  }}</strong>
                </div>
                <div>
                  <span>成交件数</span
                  ><strong>{{
                    Number(detail.unitsSold || 0).toLocaleString()
                  }}</strong>
                </div>
              </div>
              <p class="muted">
                统计起点：{{
                  dateTime(detail.statisticsStartAt)
                }}；累计数据以本地已同步 TikTok 订单为准。
              </p>
            </ElTabPane>
            <ElTabPane label="生效营销" name="marketing">
              <ElAlert
                v-if="detail.flashSaleDataStatus === 'REAUTH_REQUIRED'"
                :closable="false"
                show-icon
                title="当前店铺缺少营销资料权限，无法判定是否有秒杀活动"
                type="warning"
              >
                <template #default>
                  <ElButton
                    link
                    type="primary"
                    @click="goToUpdateAuthorization(detailShop())"
                  >
                    重新授权
                  </ElButton>
                </template>
              </ElAlert>
              <ElEmpty
                v-else-if="detail.flashSaleDataStatus === 'CONFIRMED_EMPTY'"
                description="暂无生效秒杀活动"
              />
              <ElAlert
                v-else-if="detail.flashSaleDataStatus !== 'AVAILABLE'"
                :closable="false"
                :title="flashSaleState(detail)"
                type="info"
              />
              <ElTable v-else :data="detail.activePromotions || []">
                <ElTableColumn
                  label="活动名称"
                  prop="activityTitle"
                  min-width="180"
                />
                <ElTableColumn label="SKU" prop="skuId" min-width="150" />
                <ElTableColumn label="秒杀价">
                  <template #default="{ row }">
                    {{
                      money(row.activityPrice, row.activityPrice, row.currency)
                    }}
                  </template>
                </ElTableColumn>
                <ElTableColumn
                  :label="`生效时间（${shopTimezone(detail.region).label}）`"
                  min-width="300"
                >
                  <template #default="{ row }">
                    {{ zonedDateTime(row.startTime, detail.region) }} ~
                    {{ zonedDateTime(row.endTime, detail.region) }}
                  </template>
                </ElTableColumn>
              </ElTable>
            </ElTabPane>
          </ElTabs>
        </template>
      </div>
    </ElDrawer>

    <ElDrawer v-model="syncTaskVisible" size="520px" title="正在同步商品数据">
      <div v-if="shopSyncProgress.length > 0" class="sync-task-list">
        <div
          v-for="item in shopSyncProgress"
          :key="item.shopId"
          class="sync-task-item"
        >
          <div class="sync-task-head">
            <strong>{{ item.shopName || item.shopId }}</strong>
            <ElTag type="primary" effect="plain">执行中</ElTag>
          </div>
          <ElProgress :percentage="item.percentage" />
        </div>
      </div>
      <ElEmpty v-else description="当前没有同步中的任务" />
    </ElDrawer>
  </div>
</template>

<style scoped>
.product-management {
  min-height: 100%;
  padding: 16px;
  background: #f5f7fa;
}

.filter-card,
.table-card {
  border: 0;
  border-radius: 12px;
}

.table-card {
  margin-top: 14px;
}

.authorization-alert,
.detail-alert {
  margin-bottom: 14px;
}

.batch-toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
  color: #606266;
}

.refreshing-tip {
  margin-left: auto;
  font-size: 12px;
  color: #909399;
}

.page-heading,
.sync-area,
.filters,
.advanced-filters,
.product-cell,
.detail-heading {
  display: flex;
  align-items: center;
}

.page-heading {
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.page-heading h2 {
  margin: 0 0 6px;
  font-size: 20px;
}

.page-heading p,
.detail-heading p {
  margin: 0;
  color: #909399;
}

.sync-area {
  gap: 14px;
}

.sync-buttons {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
}

.sync-task-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sync-task-item {
  padding: 14px;
  border: 1px solid #ebeef5;
  border-radius: 9px;
}

.sync-task-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.sync-task-head strong {
  display: block;
}

.filters {
  flex-wrap: wrap;
  gap: 10px;
}

.filters > :deep(.el-select) {
  width: 190px;
}

.filters > :deep(.el-input) {
  width: 260px;
}

.advanced-filters {
  flex-wrap: wrap;
  gap: 10px;
  align-items: stretch;
  padding-top: 14px;
  margin-top: 14px;
  border-top: 1px solid #ebeef5;
}

.advanced-filters > :deep(.el-input),
.advanced-filters > :deep(.el-select) {
  width: 200px;
}

.range {
  display: flex;
  gap: 7px;
  align-items: center;
  padding: 0 10px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
}

.range span {
  color: #606266;
  white-space: nowrap;
}

.range :deep(.el-input-number) {
  width: 92px;
}

.range :deep(.el-input__wrapper) {
  box-shadow: none;
}

.range i {
  color: #c0c4cc;
}

.product-cell {
  gap: 12px;
}

.product-image {
  flex: none;
  width: 64px;
  height: 64px;
  background: #f2f3f5;
  border-radius: 7px;
}

.image-placeholder {
  display: grid;
  place-items: center;
  height: 100%;
  font-size: 11px;
  color: #c0c4cc;
}

.product-copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.product-copy span,
small,
.muted {
  font-size: 12px;
  color: #909399;
}

.category-nowrap {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-category {
  max-width: 520px;
}

.flash-price {
  display: inline-block;
  padding: 2px 8px;
  font-weight: 600;
  color: #d97706;
  background: #fff7ed;
  border-radius: 999px;
}

.title-link {
  max-width: 230px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
  color: #303133;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  background: none;
  border: 0;
}

.title-link:hover {
  color: #ff6500;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.detail-body {
  min-height: 360px;
}

.detail-heading {
  gap: 16px;
  margin-bottom: 20px;
}

.detail-heading h3 {
  max-width: 760px;
  margin: 0 0 8px;
  font-size: 18px;
}

.detail-image {
  width: 88px;
  height: 88px;
  border-radius: 10px;
}

.sku-image {
  width: 44px;
  height: 44px;
  background: #f2f3f5;
  border-radius: 6px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 16px;
}

.metric-grid > div {
  padding: 22px;
  background: linear-gradient(135deg, #fff8f2, #fff);
  border: 1px solid #ebeef5;
  border-radius: 10px;
}

.metric-grid span {
  display: block;
  margin-bottom: 9px;
  color: #909399;
}

.metric-grid strong {
  font-size: 24px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 14px;
}

.image-grid > div {
  position: relative;
  overflow: hidden;
  border: 1px solid #ebeef5;
  border-radius: 9px;
}

.image-grid :deep(.el-image) {
  display: block;
  width: 100%;
  height: 150px;
}

.image-cache-placeholder {
  display: grid;
  place-items: center;
  width: 100%;
  height: 150px;
  color: #909399;
  background: linear-gradient(135deg, #f7f8fa, #eef1f5);
}

.image-cache-placeholder span {
  position: static;
  padding: 0;
  font-size: 12px;
  color: #909399;
  background: transparent;
}

.image-cache-actions {
  display: flex;
  justify-content: center;
  margin-top: 14px;
}

.product-description {
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.7;
  color: #303133;
}

.product-description :deep(img) {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 12px auto;
}
</style>
